/**
 * The form bar (R3, docs/PLAYBOOK-redesign.md §2 and §7 R3): two rows over
 * every tab of one form.
 *
 * Row 1 — the breadcrumb, the form's name and its `Live v{n}` pill; the five
 * groups Design · Automate · Distribute · Results · Settings as a segmented
 * control of links (Results carries the "{n} new" badge); and on the right
 * the unpublished indicator, Save draft, Ask AI, Get link…, Share…,
 * Preview ↗ and Publish.
 *
 * Row 2 — the current group's sub-tabs, on `SubTabs`, mapped onto the
 * EXISTING routes (§2): the labels say "Fields" and "Responses" while the
 * paths stay `build` and `submissions`.
 *
 * Nothing here decides anything about the draft: every handler is the
 * workspace's own, and the indicator only reads the save state and the
 * change count it is given. A View grant hides the groups it hid before
 * (`canEdit` / `canManage`), and so their sub-tabs.
 *
 * On a phone (under 600, `phone`) the same bar renders a different branch:
 * the "‹" back link, the name, the icons and Publish on one row, the five
 * groups full width with short labels, and the sub-tabs as a chip row.
 */
import { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import type { FormDetail, FormActivity } from '../../api/forms';
import type { Level } from '../../api/access';
import { SubTabs } from '../../components/fs';
import type { DraftState, SaveState } from './FormWorkspace';

export type FormGroup = 'design' | 'automate' | 'distribute' | 'results' | 'settings';

/** Which group a tab segment belongs to (§2). The legacy segments land where their redirects go. */
export function groupOfTab(tab: string): FormGroup {
  switch (tab) {
    case 'build': case 'widgets': case 'logic': case 'document': return 'design';
    case 'workflow': case 'followup': case 'runs': return 'automate';
    case 'access': case 'invitations': return 'distribute';
    case 'settings': case 'versions': return 'settings';
    default: return 'results';
  }
}

export interface FormBarProps {
  form: FormDetail;
  /** the latest publish's ordinal; 0 or undefined = never published */
  version?: number;
  activity?: FormActivity;
  myLevel: Level | null;
  canEdit: boolean;
  canManage: boolean;
  /** `/admin/forms/:id` */
  base: string;
  /** the current tab segment */
  tab: string;
  /** the query string Responses and Summary share */
  dataSearch: string;
  /** the public form's address, when it is published */
  publicUrl: string | null;
  phone: boolean;

  // the draft, read-only here
  state: DraftState;
  save: SaveState;
  busy: boolean;
  /** the last error the draft bar showed inline */
  error: string;
  /** what publishing would change, when known */
  changeCount: number | null;
  /** whether the working copy has loaded (nothing about the draft renders before it has) */
  hasDraft: boolean;

  /** the phone's sticky draft bar is on screen and carries the indicator, so the head need not */
  indicatorBelow?: boolean;

  onSave: () => void;
  onConflict: () => void;
  onPublish: () => void;
  onGetLink: () => void;
  onShare: () => void;
  /** the Ask AI trigger (renders nothing without an AI key) */
  assistant?: ReactNode;
}

const GROUP_LABEL: Record<FormGroup, string> = {
  design: 'Design', automate: 'Automate', distribute: 'Distribute', results: 'Results', settings: 'Settings',
};
/** The phone bar's short words (static-reference/ABC Phone Bar). */
const GROUP_SHORT: Record<FormGroup, string> = {
  design: 'Design', automate: 'Automate', distribute: 'Share', results: 'Results', settings: '⚙',
};

export default function FormBar(p: FormBarProps) {
  const { form, base, canEdit, canManage, dataSearch } = p;
  const group = groupOfTab(p.tab);
  const published = form.status === 'published';

  // The groups this person can open, and where each one lands.
  const groups: { id: FormGroup; to: string; badge?: ReactNode }[] = [];
  if (canEdit) groups.push({ id: 'design', to: `${base}/build` });
  if (canEdit) groups.push({ id: 'automate', to: `${base}/workflow` });
  if (canEdit || canManage) groups.push({ id: 'distribute', to: canEdit ? `${base}/access` : `${base}/invitations` });
  groups.push({ id: 'results', to: `${base}/submissions${dataSearch}`, badge: p.activity && p.activity.new > 0 ? `${p.activity.new} new` : undefined });
  if (canEdit) groups.push({ id: 'settings', to: `${base}/settings` });

  // The current group's sub-tabs (§2). Runs joined Automate in R6.
  const subs = (() => {
    switch (group) {
      case 'design': return canEdit ? [
        { label: 'Fields', to: `${base}/build` }, { label: 'Logic', to: `${base}/logic` }, { label: 'Document', to: `${base}/document` },
      ] : [];
      case 'automate': return canEdit ? [{ label: 'Workflow', to: `${base}/workflow` }, { label: 'Runs', to: `${base}/runs` }] : [];
      case 'distribute': return [
        ...(canEdit ? [{ label: 'Link & access', to: `${base}/access` }] : []),
        ...(canManage ? [{ label: 'Invitations', to: `${base}/invitations` }] : []),
      ];
      case 'settings': return canEdit ? [
        { label: 'Form settings', to: `${base}/settings` }, { label: 'Versions', to: `${base}/versions` },
      ] : [];
      default: return [
        { label: 'Responses', to: `${base}/submissions${dataSearch}` },
        { label: 'Summary', to: `${base}/summary${dataSearch}` },
        { label: 'Analytics', to: `${base}/analytics` },
      ];
    }
  })();

  const statusPill = (
    <span className={`fs-pill fw-live ${published ? 'ok' : form.status === 'archived' ? 'danger' : 'neutral'}`} title={published ? 'Respondents see this version' : undefined}>
      {published && <span className="fw-dot" aria-hidden="true" />}
      {published ? (p.version ? `Live v${p.version}` : 'Live') : form.status === 'archived' ? 'Archived' : 'Draft'}
    </span>
  );
  const disabledPill = form.disabled_at
    ? <span className="fs-pill warn" title="Switched off — the public link shows the closed page">Disabled</span> : null;
  const viewOnlyPill = p.myLevel === 'view'
    ? <span className="fs-pill identity" title="Editing, sharing and deleting need Edit or Manage">View only</span> : null;

  const groupsNav = (
    <nav className="fs-seg fw-groups" aria-label="Form areas">
      {groups.map((g) => (
        <NavLink
          key={g.id} to={g.to} className={g.id === group ? 'on' : ''} aria-current={g.id === group ? 'page' : undefined}
          title={GROUP_LABEL[g.id]}
        >
          {p.phone ? GROUP_SHORT[g.id] : GROUP_LABEL[g.id]}
          {g.badge != null && <span className="badge">{g.badge}</span>}
        </NavLink>
      ))}
    </nav>
  );

  const getLink = published && (
    <button className="fs-btn icon fw-icon" title="Get link…" aria-label="Get link…" onClick={p.onGetLink}><LinkGlyph /></button>
  );
  const share = canManage && (
    <button className="fs-btn icon fw-icon" title="Share…" aria-label="Share…" onClick={p.onShare}><PeopleGlyph /></button>
  );
  const preview = published && p.publicUrl && (
    <a className="fs-btn text fw-preview" href={p.publicUrl} target="_blank" rel="noreferrer" title="Open the live form in a new tab">
      Preview ↗
    </a>
  );
  const publish = canEdit && p.hasDraft && (
    <button className={`fs-btn primary${p.phone ? ' sm' : ''}`} disabled={p.busy} onClick={p.onPublish}>Publish</button>
  );
  const indicator = canEdit && p.hasDraft && (
    <UnpublishedIndicator
      form={form} state={p.state} save={p.save} error={p.error} changeCount={p.changeCount}
      onOpen={p.onPublish} onSave={p.onSave} onConflict={p.onConflict}
    />
  );

  if (p.phone) {
    return (
      <div className="fw-bar phone">
        <div className="fw-prow">
          <Link className="fw-back" to="/admin" aria-label="Back to forms">‹</Link>
          <div className="fw-ident">
            <b className="fw-name">{form.name}</b>
            <span className="fw-pills">{statusPill}{disabledPill}{viewOnlyPill}{!p.indicatorBelow && indicator}</span>
          </div>
          {p.assistant}
          {getLink}
          {share}
          {publish}
        </div>
        {groupsNav}
        {subs.length > 0 && <SubTabs className="fw-subtabs" aria-label={`${GROUP_LABEL[group]} pages`} items={subs} />}
      </div>
    );
  }

  return (
    <div className="fw-bar">
      <div className="fw-row1">
        <div className="fw-ident">
          <Link className="fw-crumb" to={form.folder_id ? `/admin?folder=${form.folder_id}` : '/admin'}>
            Forms{form.folder_name ? ` / ${form.folder_name}` : ''}
          </Link>
          <div className="fw-namerow">
            <b className="fw-name">{form.name}</b>
            {statusPill}{disabledPill}{viewOnlyPill}
          </div>
        </div>
        <span className="fw-gap" />
        {groupsNav}
        <span className="fw-gap" />
        <div className="fw-actions">
          {indicator}
          {canEdit && p.hasDraft && p.state !== 'clean' && (
            <button
              className="fs-btn text sm fw-savebtn" disabled={p.busy || p.save.kind === 'saving'} onClick={p.onSave}
              title="Drafts save themselves; this saves right now (Ctrl+S)"
            >
              Save draft
            </button>
          )}
          {p.assistant}
          {getLink}
          {share}
          {preview}
          {publish}
        </div>
      </div>
      {subs.length > 0 && <SubTabs className="fw-subtabs" aria-label={`${GROUP_LABEL[group]} pages`} items={subs} />}
    </div>
  );
}

/** "just now", "3 min ago"… — for the saved pill. */
function ago(at: number, now: number): string {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 45) return 'just now';
  if (s < 90) return 'a minute ago';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h === 1 ? 'an hour ago' : `${h} h ago`;
}

/**
 * The design's "{n} unpublished" / "All changes live", which opens the
 * Publish panel, with the autosave's own word beside it.
 *
 * The count is what the server said publishing would change, refreshed
 * after every save; between an edit and its save it is the last known one,
 * and the word beside it ("Saving…") says why. The autosave's states are the
 * draft bar's, verbatim: at rest it says nothing; a failure or a conflict is
 * a button, because the next move is the person's.
 */
export function UnpublishedIndicator({ form, state, save, error, changeCount, onOpen, onSave, onConflict, now = Date.now() }: {
  form: FormDetail; state: DraftState; save: SaveState; error: string; changeCount: number | null;
  onOpen: () => void; onSave: () => void; onConflict: () => void;
  /** the clock, for the "Saved · ago" word; the bar passes a ticking one */
  now?: number;
}) {
  const never = form.status !== 'published';
  const n = changeCount;
  let text: string;
  let tone: 'warn' | 'ok' | 'muted';
  if (never) { text = n ? `Not published yet · ${n} ${n === 1 ? 'change' : 'changes'}` : 'Not published yet'; tone = 'warn'; }
  else if (n && n > 0) { text = `${n} unpublished`; tone = 'warn'; }
  else if (n === 0 && state === 'clean') { text = 'All changes live'; tone = 'ok'; }
  else if (n === 0) { text = 'Draft matches live'; tone = 'muted'; }
  else if (state !== 'clean') { text = 'Unpublished changes'; tone = 'warn'; }
  else { text = 'All changes live'; tone = 'ok'; }

  const resting = state === 'clean' && save.kind === 'idle';
  const word: { cls: string; text: string; title?: string; onClick?: () => void } | null =
    resting
      ? null
      : save.kind === 'conflict'
        ? { cls: 'danger', text: 'Not saved — someone else changed this form', onClick: onConflict, title: 'Choose whose draft to keep' }
        : save.kind === 'failed'
          ? { cls: 'danger', text: 'Save failed — retry', onClick: onSave, title: save.message }
          : save.kind === 'pending' || save.kind === 'saving'
            ? { cls: 'neutral', text: 'Saving…', title: 'The draft saves itself two seconds after your last change' }
            : save.kind === 'saved'
              ? { cls: 'info', text: `Saved · ${ago(save.at, now)}`, title: 'Nothing a respondent sees changes until you publish' }
              : state === 'draft'
                ? { cls: 'info', text: 'Draft saved', title: 'Nothing a respondent sees changes until you publish' }
                : { cls: 'warn', text: 'Unsaved changes' };

  return (
    <span className="fw-status">
      {error && <span className="fs-pill danger fw-err" title={error}>{error}</span>}
      <button type="button" className={`fw-unpub ${tone}`} onClick={onOpen} title="Open the Publish panel">{text}</button>
      {word && (word.onClick
        ? <button type="button" className={`fs-pill ${word.cls} fw-saveword`} title={word.title} onClick={word.onClick}>{word.text}</button>
        : <span className={`fs-pill ${word.cls} fw-saveword`} title={word.title} role="status">{word.text}</span>)}
    </span>
  );
}

function LinkGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <path d="M6.5 9.5a3 3 0 0 0 4.2 0l2-2a3 3 0 0 0-4.2-4.2l-1 1" />
      <path d="M9.5 6.5a3 3 0 0 0-4.2 0l-2 2a3 3 0 0 0 4.2 4.2l1-1" />
    </svg>
  );
}
function PeopleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <circle cx="6" cy="5.5" r="2.5" />
      <path d="M1.5 13.5c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" />
      <path d="M11 3.5a2.5 2.5 0 0 1 0 4M12.5 9.7c1.3.5 2 1.7 2 3.3" />
    </svg>
  );
}
